import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Phone, PhoneIncoming, Plus, X } from 'lucide-react'

import { api } from '@/api/client'
import { Button } from '@/components/ui'

export type IncomingCall = {
  callId: string
  fromNumber: string
}

export function IncomingCallCard({
  call,
  onClose,
  dragHandleProps,
}: {
  call: IncomingCall
  onClose: () => void
  dragHandleProps?: React.HTMLAttributes<HTMLDivElement>
}) {
  const navigate = useNavigate()
  const lookupQuery = useQuery({
    queryKey: ['mango-lookup', call.fromNumber],
    queryFn: () => api.mangoLookup(call.fromNumber),
    staleTime: 60_000,
  })

  const applications = lookupQuery.data?.applications ?? []
  const create = lookupQuery.data?.create ?? null

  const openApplication = (path: string) => {
    onClose()
    navigate(path)
  }

  const createApplication = () => {
    if (!create) {
      return
    }
    onClose()
    navigate(
      `/create-application/${create.schemaId}/${create.stageId}?phone=${encodeURIComponent(call.fromNumber)}`,
    )
  }

  return (
    <div className="w-[360px] rounded-[28px] border border-white/70 bg-white/95 p-5 shadow-panel backdrop-blur">
      <div className="flex items-start justify-between">
        <div
          className="flex flex-1 cursor-grab select-none items-center gap-3 touch-none active:cursor-grabbing"
          {...dragHandleProps}
        >
          <div className="rounded-2xl bg-blue-50 p-2.5 text-blue-600">
            <PhoneIncoming className="h-5 w-5" />
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-ink-400">Входящий звонок</p>
            <p className="text-lg font-bold text-ink-950">{call.fromNumber || 'Неизвестный номер'}</p>
          </div>
        </div>
        <button
          className="rounded-xl p-1.5 text-ink-400 transition hover:bg-ink-50 hover:text-ink-700"
          onClick={onClose}
          type="button"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-4 max-h-72 space-y-2 overflow-y-auto">
        {lookupQuery.isLoading ? (
          <p className="text-sm text-ink-400">Ищем заявки…</p>
        ) : applications.length ? (
          applications.map((application) => (
            <button
              className="block w-full rounded-2xl border border-ink-100 bg-slate-50 p-3 text-left transition hover:border-accent-300 hover:bg-white"
              key={application.id}
              onClick={() => openApplication(application.path)}
              type="button"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-sm font-semibold text-ink-900">Заявка #{application.id}</span>
                {application.stageName ? (
                  <span className="truncate text-xs text-ink-400">{application.stageName}</span>
                ) : null}
              </div>
              {application.fields.map((field) => (
                <p className="mt-1 text-xs text-ink-500" key={field.name}>
                  <span className="text-ink-400">{field.name}:</span> {field.value || '—'}
                </p>
              ))}
            </button>
          ))
        ) : (
          <p className="text-sm text-ink-400">Заявок по этому номеру не найдено.</p>
        )}
      </div>

      {create ? (
        <Button className="mt-4 w-full" onClick={createApplication} tone="primary">
          <Plus className="mr-2 h-4 w-4" />
          Создать заявку
        </Button>
      ) : (
        <p className="mt-4 flex items-center gap-2 text-xs text-ink-400">
          <Phone className="h-3.5 w-3.5" />
          Интеграция Mango не настроена для создания заявки
        </p>
      )}
    </div>
  )
}
